import { useState, type ReactNode } from 'react'
import { NUTRITION_GUIDE_REFERENCES } from '../data/references'

interface Screen1Props {
  onGetStartedGuest: () => void
  onCreateAccount: () => void
  onLogin: () => void
}

const FEATURE_ICONS: Record<string, ReactNode> = {
  'Personalised guidance': (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M2.5 13c0-3.038 2.462-5 5.5-5s5.5 1.962 5.5 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  ),
  'Timed to your sessions': (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="9" r="5.5" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M8 6.5V9l2 1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M6 1.5h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  ),
  'Evidence-based': (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="3" y="2" width="8" height="11" rx="1" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M5.5 5.5h4M5.5 8h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <path d="M11 8l2 2-2 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  'Track & adjust': (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2 12l3.5-4 3 2.5L12 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="12" cy="5" r="1.5" fill="currentColor"/>
    </svg>
  ),
}

const FEATURES = [
  { title: 'Personalised guidance', description: 'Based on age, weight & dietary needs' },
  { title: 'Timed to your sessions', description: 'Right food at the right time' },
  { title: 'Evidence-based', description: 'Built on sports nutrition science' },
  { title: 'Track & adjust', description: 'See what works for your child' },
]

export function Screen1({ onGetStartedGuest, onCreateAccount, onLogin }: Screen1Props) {
  const [showReferences, setShowReferences] = useState(false)

  return (
    <div className="logging-screen min-h-screen flex flex-col">
      <div className="mx-auto w-full max-w-[430px] flex flex-col flex-1">
        <header className="log-font-heading px-5 py-5 flex items-center justify-center gap-2 border-b border-[var(--log-ink-10)]">
          <img src="/favicon/favicon-96x96.png" alt="" aria-hidden="true" className="w-7 h-7 object-contain" />
          <span className="text-[17px] font-bold tracking-tight text-[var(--log-ink)]">EnergyMatch</span>
        </header>

        <main className="flex-1 px-5 pb-64 overflow-y-auto">
          <div className="pt-10">
            <h1 className="log-font-heading text-[32px] font-bold tracking-tight text-[var(--log-ink)] leading-tight">
              Active kids need<br />smart fuel
            </h1>
            <p className="mt-3 text-[15px] text-[var(--log-ink-60)] leading-relaxed">
              EnergyMatch tells you what to feed them and when, so they perform their best
            </p>
          </div>

          <div className="mt-8 grid grid-cols-2 grid-rows-2 gap-2">
            {FEATURES.map((f) =>
              f.title === 'Evidence-based' ? (
                <button
                  key={f.title}
                  type="button"
                  onClick={() => setShowReferences(true)}
                  className="h-full min-h-0 rounded-[var(--log-radius)] bg-[var(--log-card)] p-3 shadow-[var(--log-shadow)] text-left focus:outline-none focus:ring-2 focus:ring-[var(--log-green-mid)] focus:ring-offset-2 flex flex-col"
                >
                  <span className="mb-1.5 text-[var(--log-green-mid)]">{FEATURE_ICONS[f.title]}</span>
                  <p className="log-font-heading text-[13px] font-bold text-[var(--log-ink)] leading-tight">
                    {f.title} <span className="text-[var(--log-green-mid)]">↗</span>
                  </p>
                  <p className="mt-0.5 text-[11px] text-[var(--log-ink-60)] leading-snug">{f.description}</p>
                </button>
              ) : (
                <div key={f.title} className="h-full min-h-0 rounded-[var(--log-radius)] bg-[var(--log-card)] p-3 shadow-[var(--log-shadow)] flex flex-col">
                  <span className="mb-1.5 text-[var(--log-green-mid)]">{FEATURE_ICONS[f.title]}</span>
                  <p className="log-font-heading text-[13px] font-bold text-[var(--log-ink)] leading-tight">{f.title}</p>
                  <p className="mt-0.5 text-[11px] text-[var(--log-ink-60)] leading-snug">{f.description}</p>
                </div>
              )
            )}
          </div>
        </main>

        <nav className="log-bottom-nav flex flex-col gap-2">
          <button type="button" className="log-nav-btn primary w-full" onClick={onLogin}>
            Log in
          </button>
          <button type="button" className="log-nav-btn secondary w-full" onClick={onCreateAccount}>
            Create account
          </button>
          <button
            type="button"
            className="w-full py-2 text-[13px] font-medium text-[var(--log-ink-60)] hover:text-[var(--log-ink)] transition-colors"
            onClick={onGetStartedGuest}
          >
            Continue without an account
          </button>
          <p className="text-center text-[11px] text-[var(--log-ink-30)] leading-snug">
            An account keeps meal logs and session feedback improving<br />nutrition recommendations over time, and supports multiple children.
          </p>
          <div className="flex items-center justify-center gap-3 pt-1">
            <a
              href="/privacy-policy.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-[var(--log-ink-30)] hover:text-[var(--log-ink-60)] underline underline-offset-2 transition-colors"
            >
              Privacy Policy
            </a>
            <span className="text-[var(--log-ink-10)]" aria-hidden>·</span>
            <a
              href="/terms-of-service.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-[var(--log-ink-30)] hover:text-[var(--log-ink-60)] underline underline-offset-2 transition-colors"
            >
              Terms of Service
            </a>
          </div>
        </nav>
      </div>

      {showReferences && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="references-heading"
          onClick={() => setShowReferences(false)}
        >
          <div
            className="max-h-[85vh] w-full max-w-[400px] overflow-hidden rounded-[var(--log-radius)] bg-[var(--log-card)] shadow-[var(--log-shadow-lg)] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[var(--log-ink-10)] px-4 py-3">
              <h2 id="references-heading" className="log-font-heading text-[15px] font-bold text-[var(--log-ink)]">
                References
              </h2>
              <button
                type="button"
                onClick={() => setShowReferences(false)}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center text-[var(--log-ink-60)] hover:text-[var(--log-ink)] focus:outline-none focus:ring-2 focus:ring-[var(--log-green-mid)] rounded-[var(--log-radius-sm)] px-2"
                aria-label="Close references"
              >
                Close
              </button>
            </div>
            <div className="overflow-y-auto px-4 py-3 flex-1">
              <p className="mb-3 text-[13px] text-[var(--log-ink-60)]">
                These nutritional guidelines are not medical advice. They are based on the following sources.
              </p>
              <ol className="list-decimal list-inside space-y-3 text-[13px] text-[var(--log-ink)]">
                {NUTRITION_GUIDE_REFERENCES.map((ref, i) => (
                  <li key={i} className="pl-2">
                    {typeof ref === 'string' ? ref : (
                      <>{ref.text}{' '}
                        <a href={ref.url} target="_blank" rel="noopener noreferrer" className="text-[var(--log-green-mid)] underline underline-offset-2 break-all">
                          View PDF
                        </a>
                      </>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
